/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable quote-props */
import React, { useState, useEffect, useContext } from "react";
import { Box, Stack, styled, Switch } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { BreakpointContext } from "components/common";
import { BoostMobilePricingTable, BoostPricingTable, ProductTermToggle } from "@tomocredit/common-fe/components";
import { calculatePerMonthPrice, isValidEmail, trackLead } from "@tomocredit/common-fe/utils";
import { BOOST_PRICING_DEFAULT_TERM, BOOST_SUBSCRIPTION_FEATURES, boostProductTierConfig_v3, boostTiers, LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import { BoostTerm, BoostTier } from "@tomocredit/common-fe/types";
import GradientButton from "@tomocredit/common-fe/components/common/GradientButton";
import TestimonialPricing from "images/boost/testimonal-pricing.webp";
import RightArrowIcon from "images/arrow-right-black.svg";
import * as braze from "@braze/web-sdk";
import BoostContainer from "./components/BoostContainer";
import CreditImpact from "./components/GoodCreditImpact";
import "./BoostLandingStyle.css";
import EmailInputPopup from "./components/EmailInputPopup";

const boostFeaturesToDisplay = [
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_LINE,
  BOOST_SUBSCRIPTION_FEATURES.NO_HARD_PULL,
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_MONITORING,
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_BOOST,
  BOOST_SUBSCRIPTION_FEATURES.RENT_REPORTING,
  BOOST_SUBSCRIPTION_FEATURES.AI_FINANCIAL_ASSISTANT,
  BOOST_SUBSCRIPTION_FEATURES.CREDIT_REPORT,
];

const IOSSwitch = styled((props: any) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(170, 146, 249, 1)',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const BoostPricing = () => {
  const { isBelow } = useContext(BreakpointContext);
  const location = useLocation();
  const history = useHistory();
  const belowMd = isBelow("md"); // 768

  const queryParameters = new URLSearchParams(window.location.search);
  const tierRaw = queryParameters.get("tier");
  const termRaw = queryParameters.get("term") || "";
  const emailFromQueryParam = queryParameters.get("email") || "";
  const referralCode = queryParameters.get("referralCode") || "";

  const tierFromQueryParam = (tierRaw || "") in boostTiers ? tierRaw : null;
  const termFromQueryParam = [BoostTerm.YEARLY, BoostTerm.MONTHLY].includes(
    termRaw as BoostTerm,
  )
    ? termRaw
    : null;

  const [checkoutEmail, setCheckoutEmail] = useState(emailFromQueryParam);
  const [isEmailInputVisible, setIsEmailInputVisible] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState<BoostTier>(
    (tierFromQueryParam || boostTiers.VIP) as BoostTier,
  );
  const [checkoutTerm, setCheckoutTerm] = useState<BoostTerm>(
    (termFromQueryParam || BOOST_PRICING_DEFAULT_TERM) as BoostTerm,
  );

  const tierConfig = boostProductTierConfig_v3[checkoutTerm]?.[checkoutTier];
  const { name, price, originalPrice } = tierConfig || {};
  const { perMonthPrice } = calculatePerMonthPrice(
    checkoutTerm,
    price,
    originalPrice,
  );

  const tiers: BoostTier[] = [
    BoostTier.VIP,
    BoostTier.PREMIUM,
    BoostTier.BASIC,
  ];

  const redirectToCheckout = (
    tier: BoostTier,
    term: BoostTerm,
    email: string,
    promoCode: string | null,
  ) => {
    trackLead({ email, tier, boostTerm: term });
    braze.logCustomEvent("checkout_plan_selected", { term, tier });
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("term", term);
    urlParams.set("tier", tier);
    urlParams.set("email", email);
    if (promoCode) {
      urlParams.set("referralCode", promoCode);
    }
    const checkoutUrlNew = `/boost-checkout-new?${urlParams.toString()}`;
    history.push(checkoutUrlNew);
  };

  const buttonClickCallback = async (tier: BoostTier, term: BoostTerm, userCheckoutEmail: string) => {
    window?.gtag?.("event", `LANDING_PRICING_TIER_${tier}_CLICKED`, {
      source: LEAD_SOURCES.BOOST_PRICING,
    });
    if (userCheckoutEmail && isValidEmail(userCheckoutEmail)) {
      const appliedReferralCode = referralCode || null;
      redirectToCheckout(tier, term, userCheckoutEmail, appliedReferralCode);
    } else {
      setCheckoutTier(tier);
      setCheckoutTerm(term);
      setIsEmailInputVisible(true);
    }
  };

  const proceedToCheckout = (email: string) => {
    setIsEmailInputVisible(false);
    setCheckoutEmail(email);
    buttonClickCallback(checkoutTier, checkoutTerm, email);
  };

  // when our banner history pushes from this page
  // we need this useEffect to ensure we update params from history.push
  useEffect(() => {
    const tierRawFromParam = queryParameters.get("tier");
    const termRawFromParam = queryParameters.get("term") || "";
    const tierCleansed = (tierRawFromParam || "") in boostTiers ? tierRaw : null;
    const termCleansed = [BoostTerm.YEARLY, BoostTerm.MONTHLY].includes(
      termRawFromParam as BoostTerm,
    )
      ? termRaw
      : null;

    setCheckoutTier((tierCleansed || boostTiers.VIP) as BoostTier);
    setCheckoutTerm((termCleansed || BOOST_PRICING_DEFAULT_TERM) as BoostTerm);
  }, [location]);

  // Select centered tier if previously-selected tier doesn't exist due to term change
  useEffect(() => {
    if (!tiers.includes(checkoutTier)) {
      setCheckoutTier(tiers[1]!);
    }
  }, [checkoutTerm]);

  // Mobile view component
  const renderMobileView = () => (
    <Stack
      spacing={3}
      sx={{
        width: "100%",
        marginBottom: "2rem",
      }}
    >
      <Stack
        sx={{
          placeItems: "start",
        }}
      >
        <p className="text-2xl">The AI Credit Engine that</p>
        <p className="text-2xl font-bold">Manages & Builds Your Credit</p>
      </Stack>
      <div className="w-full grid place-items-center">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <p>Monthly</p>
          <IOSSwitch checked={checkoutTerm === BoostTerm.YEARLY} onClick={() => {
            if(checkoutTerm === BoostTerm.YEARLY) {
              setCheckoutTerm(BoostTerm.MONTHLY);
            } else {
              setCheckoutTerm(BoostTerm.YEARLY);
            }
          }} />
          <p className="font-bold">Annual</p>
        </Stack>
      </div>
      <BoostMobilePricingTable
        tiers={tiers.slice().reverse()}
        checkoutTier={checkoutTier}
        checkoutTerm={checkoutTerm}
        setCheckoutTier={setCheckoutTier}
      />
      <Box
        sx={{
          position:"sticky",
          bottom:"2rem",
          width: "100%",
          boxShadow:"0px -4px 12px 0px rgba(0, 0, 0, 0.70), 0px 32px 32px 32px rgba(0, 0, 0, 0.85)",
          borderRadius: 5,
        }}
      >
        <GradientButton
          clickMethod={() => buttonClickCallback(checkoutTier, checkoutTerm, checkoutEmail)}
        >
          <span className="flex flex-row ignore text-black text-lg font-bold">
            Get {name === "Basic" ? "Starter" : name} for {perMonthPrice.toFixed(2)} a month&nbsp;
            <img alt="" src={RightArrowIcon} color="red" />
          </span>
        </GradientButton>
      </Box>
    </Stack>
  );

  // Desktop view component
  const renderDesktopView = () => (
    <>
      <div className="mt-3 md:mt-0 flex flex-col items-center">
        <div
          className="text-center mb-6"
          style={{ fontSize: 28, width: undefined }}
        >
          Meet the AI Credit Engine that{" "}
          <span className="font-bold text-boost-primary-200">
            Manages & Builds
          </span>{" "}
          Your Credit
        </div>
        <img
          src={TestimonialPricing}
          alt=""
          style={{
            objectFit: "cover",
            height: 114,
            width: "auto",
          }}
        />
      </div>
      <div className="pb-4">
        <ProductTermToggle
          checkoutTerm={checkoutTerm}
          setCheckoutTerm={setCheckoutTerm}
          belowMd={belowMd}
        />
      </div>
      <BoostPricingTable
        tiers={tiers}
        highlightTier={boostTiers.VIP}
        checkoutTerm={checkoutTerm}
        buttonClickCallback={(tier: BoostTier, term: BoostTerm) => buttonClickCallback(tier, term, checkoutEmail)}
        isMobileView={isBelow("lg")}
        selectedFeatures={Object.keys(BOOST_SUBSCRIPTION_FEATURES).filter((f) =>
          boostFeaturesToDisplay.includes(f),
        )}
        headerComponent={
          <h2 className="hidden text-white lg:flex mb-10 self-start text-4xl font-bold">
            Choose the plan that’s right for you.
          </h2>
        }
        isSecondaryJoinVisible={false}
      />
      <CreditImpact className="mt-38 lg:mt-20 max-w-[1244px]" />
    </>
  );

  return (
    <div className="w-full h-full relative">
      <BoostContainer relative disablePopup background="pricing">
        <div className="mx-auto text-white flex flex-col items-center h-full w-full mdl:py-20 z-50 space-y-10 p-6">
          {belowMd ? renderMobileView() : renderDesktopView()}
        </div>
        <EmailInputPopup
          isVisible={isEmailInputVisible}
          onClose={() => setIsEmailInputVisible(false)}
          onSubmit={proceedToCheckout}
          name={name}
          price={perMonthPrice}
        />
      </BoostContainer>
    </div>
  );
};

export default BoostPricing;
